import React from 'react';
import { Empty, Tag } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getDashboardWidgetQuery } from '@src/util/query/dashboardLayout.query';
import { MetricTile, PanelCard, useDashboard } from './widgetShell';
import { RangeBadge, rangeLabel, rangeVariables } from './widgetRange';
import { scheduleColumns } from './dashboardDetailHost';

// Widgets over the whole business - keys, mobile services, incidents, complaints, attendance gaps,
// punctuality, SIA licences and the Overview's headline figures. Figures come from wfc-server's
// getDashboardWidget (dashboardInsights.service.ts), which spells out how each one is counted.

const useWidget = (widget, settings, fallback = 'monthly', skip = false) => {
  const { data, loading } = useQuery(getDashboardWidgetQuery, {
    variables: { widget, ...(settings === null ? {} : rangeVariables(settings, fallback)) },
    fetchPolicy: 'cache-and-network',
    skip,
  });
  return { response: data?.getDashboardWidget?.response, loading: loading && !data };
};

// "this month", "this year", … for a sentence
const periodText = (settings, fallback) => {
  const range = settings?.range || fallback;
  return { today: 'today', weekly: 'this week', monthly: 'this month', yearly: 'this year', all: 'in total' }[range] || `in ${rangeLabel(settings, fallback)}`;
};
const fmtDate = (d) => (d ? dayjs(d).format('DD MMM YYYY') : '-');
const fmtDateTime = (d) => (d ? dayjs(d).format('DD MMM, HH:mm') : '-');
const TREND_RANGES = ['weekly', 'monthly', 'yearly', 'custom'];
const COLORS = { onTime: '#2e9e5b', late: '#d9534f', early: '#3b82c4', missed: '#9aa6b2', count: '#126E7D' };
const chartTip = { background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '12px' };

// ───────────────────────────── keys

const KEY_GROUPS = {
  withStaff: 'With our staff',
  withSuppliers: 'With suppliers',
  inHouse: 'In house',
  awaitingReceipt: 'Awaiting receipt from customer',
  notAssigned: 'Not assigned to anyone',
  returnRequested: 'Waiting for return',
  returnOverdue: 'Return overdue',
  issuedToday: 'Issued to staff today',
  lost: 'Lost',
  auditOverdue: 'Audit overdue',
};
const keyColumns = (group) => [
  { key: 'serial', label: 'Key', width: '1.2fr', render: (r) => r.keySerial || '-' },
  { key: 'site', label: 'Site', width: '1.4fr', render: (r) => r.siteName || '-' },
  { key: 'holder', label: group === 'issuedToday' ? 'Issued to' : 'Held by', width: '1.3fr', render: (r) => r.holder || '-' },
  group === 'returnRequested' || group === 'returnOverdue'
    ? { key: 'back', label: 'Expected back', width: '1fr', render: (r) => <span style={{ color: r.expectedBack && dayjs(r.expectedBack).isBefore(dayjs(), 'day') ? '#d9534f' : undefined }}>{fmtDate(r.expectedBack)}</span> }
    : group === 'auditOverdue'
      ? { key: 'audit', label: 'Audit due', width: '1fr', render: (r) => fmtDate(r.auditDue) }
      : { key: 'since', label: group === 'issuedToday' ? 'At' : 'Since', width: '1fr', render: (r) => (group === 'issuedToday' ? fmtDateTime(r.since) : fmtDate(r.since)) },
];
const keyHref = (r) => (r?.keyId ? `/user/keyDetail/${r.keyId}` : null);

const useOpenKeys = (response) => {
  const { openRows } = useDashboard();
  return (group) => openRows({
    title: KEY_GROUPS[group],
    columns: keyColumns(group),
    rows: response?.rows?.[group] || [],
    emptyText: 'No keys here.',
    rowHref: keyHref,
  });
};

/** Every live key by where it is (these add up to the total), and what needs chasing */
export const KeysSummaryWidget = () => {
  const navigate = useNavigate();
  const { response } = useWidget('keys', null);
  const open = useOpenKeys(response);
  const c = response?.counts || {};
  const line = (group, tone) => (
    <button type="button" key={group} className={`ins-line${tone && c[group] ? ` ${tone}` : ''}`} onClick={() => open(group)} data-key-group={group}>
      <span>{KEY_GROUPS[group]}</span><b>{c[group] ?? '…'}</b><RightOutlined />
    </button>
  );
  return (
    <PanelCard title="Key holding" extra={<span className="dw-link" onClick={() => navigate('/user/keyRegister')}>Key Register <RightOutlined style={{ fontSize: 10 }} /></span>}>
      <div className="dw-count-line"><b data-metric-value>{response ? response.total : '…'}</b> live keys</div>
      <div className="ins-lines dw-scroll">
        {['withStaff', 'withSuppliers', 'inHouse', 'awaitingReceipt', 'notAssigned'].filter((g) => g !== 'notAssigned' || c.notAssigned).map((g) => line(g))}
        <div className="ins-sep" />
        {line('returnRequested', 'warn')}
        {line('returnOverdue', 'danger')}
        {line('issuedToday')}
        {line('lost', 'danger')}
        {line('auditOverdue', 'warn')}
      </div>
    </PanelCard>
  );
};

const KEY_TILES = {
  withStaff: { label: 'Keys with our staff' },
  returnRequested: { label: 'Keys waiting for return', alertOn: 'returnOverdue', sub: (c) => (c.returnOverdue ? `${c.returnOverdue} overdue` : 'none overdue') },
  issuedToday: { label: 'Keys issued to staff today' },
  inHouse: { label: 'Keys in house' },
};
export const KeyFigureWidget = ({ metric }) => {
  const { response, loading } = useWidget('keys', null);
  const open = useOpenKeys(response);
  const meta = KEY_TILES[metric];
  const c = response?.counts || {};
  return (
    <MetricTile
      label={meta.label}
      value={response ? c[metric] : undefined}
      loading={loading}
      alert={!!(meta.alertOn && c[meta.alertOn])}
      onClick={() => open(metric === 'returnRequested' && c.returnOverdue ? 'returnOverdue' : metric)}
      hint="View keys"
      sub={response && meta.sub ? meta.sub(c) : null}
    />
  );
};

// ───────────────────────────── mobile services

export const MobileHoursWidget = ({ settings, onSettings }) => {
  const navigate = useNavigate();
  const { response, loading } = useWidget('mobileServices', settings);
  return (
    <MetricTile
      label="Mobile service hours"
      value={response ? `${response.hours} h` : undefined}
      loading={loading}
      badge={<RangeBadge settings={settings} onChange={onSettings} fallback="monthly" options={['today', 'weekly', 'monthly', 'yearly', 'custom']} />}
      onClick={() => navigate('/user/mobileServices')}
      hint="Open Mobile Services"
      sub={response ? `${response.jobs} jobs · ${response.completedHours} h completed` : null}
    />
  );
};

export const MobileOverdueWidget = () => {
  const { openRows } = useDashboard();
  const { response, loading } = useWidget('mobileServices', null);
  const columns = [
    { key: 'site', label: 'Site', width: '1.4fr', render: (r) => r.siteName || '-' },
    { key: 'type', label: 'Service', width: '1.2fr', render: (r) => r.dutyType || '-' },
    { key: 'when', label: 'Due', width: '1.3fr', render: (r) => `${fmtDateTime(r.startTime)} – ${r.endTime ? dayjs(r.endTime).format('HH:mm') : ''}` },
    { key: 'supplier', label: 'Supplier', width: '1fr', render: (r) => r.supplier || '-' },
    { key: 'status', label: '', width: '0.7fr', render: (r) => (r.status === 'overdue' ? <Tag color="red">Overdue</Tag> : <Tag color="orange">Due</Tag>) },
  ];
  return (
    <MetricTile
      label="Mobile jobs overdue"
      value={response ? response.overdue : undefined}
      loading={loading}
      alert={response?.overdue > 0}
      badge={<span className="metric-badge">Now</span>}
      onClick={() => openRows({ title: 'Mobile jobs overdue or due', columns, rows: response?.rows || [], emptyText: 'Nothing overdue or due.', rowHref: () => '/user/mobileServices' })}
      sub={response ? `${response.due} due now` : null}
    />
  );
};

// ───────────────────────────── incidents & complaints

export const IncidentsTrendWidget = ({ settings, onSettings }) => {
  const navigate = useNavigate();
  const { response } = useWidget('incidents', settings, 'yearly');
  const trend = response?.trend || [];
  return (
    <PanelCard title="Incidents" extra={<RangeBadge settings={settings} onChange={onSettings} fallback="yearly" options={TREND_RANGES} />}>
      <div className="ins-head-figures" onClick={() => navigate('/user/incidentReporting')}>
        <span><b data-metric-value>{response ? response.total : '…'}</b> {periodText(settings, 'yearly')}</span>
        <span className={response?.open ? 'danger' : ''}><b>{response ? response.open : '…'}</b> still open</span>
        {(response?.byType || []).slice(0, 3).map((t) => <Tag key={t.type}>{t.type} {t.count}</Tag>)}
      </div>
      {trend.some((x) => x.count) ? (
        <div className="ins-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: '#8c8c8c', fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: '#8c8c8c', fontSize: 11 }} width={28} />
              <Tooltip formatter={(v) => [v, 'Incidents']} contentStyle={chartTip} />
              <Bar dataKey="count" fill="#d9534f" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : <div className="dw-empty"><Empty description={response ? 'No incidents in this period.' : 'Loading…'} image={Empty.PRESENTED_IMAGE_SIMPLE} /></div>}
    </PanelCard>
  );
};

export const OpenIncidentsWidget = () => {
  const navigate = useNavigate();
  const { response, loading } = useWidget('incidents', null);
  return <MetricTile label="Open incidents" value={response ? response.open : undefined} loading={loading} alert={response?.open > 0} badge={<span className="metric-badge">Now</span>} onClick={() => navigate('/user/incidentReporting')} hint="Open Incident Reporting" />;
};

export const OpenComplaintsWidget = () => {
  const navigate = useNavigate();
  const { response, loading } = useWidget('complaints', null);
  return <MetricTile label="Open complaints" value={response ? response.open : undefined} loading={loading} alert={response?.open > 0} badge={<span className="metric-badge">Now</span>} onClick={() => navigate('/user/complaint')} hint="Open Complaint Register" />;
};

// ───────────────────────────── attendance gaps

export const AttendanceGapsWidget = ({ settings, onSettings }) => {
  const { openDetail } = useDashboard();
  const { response } = useWidget('attendanceGaps', settings, 'monthly');
  const trend = response?.trend || [];
  const open = () => openDetail('schedule', { title: 'Attendance gaps', columns: scheduleColumns, emptyText: 'No attendance gaps in this period.', variables: { ids: response?.ids || [] } });
  return (
    <PanelCard title="Attendance gaps" extra={<RangeBadge settings={settings} onChange={onSettings} fallback="monthly" options={TREND_RANGES} />}>
      <div className="ins-head-figures clickable" onClick={open} data-hint="View duties">
        <span className={response?.total ? 'danger' : ''}><b data-metric-value>{response ? response.total : '…'}</b> past duties missing pay or bill times {periodText(settings, 'monthly')}</span>
      </div>
      {trend.some((x) => x.count) ? (
        <div className="ins-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: '#8c8c8c', fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: '#8c8c8c', fontSize: 11 }} width={28} />
              <Tooltip formatter={(v) => [v, 'Duties with gaps']} contentStyle={chartTip} />
              <Bar dataKey="count" fill="#fb8c5f" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : <div className="dw-empty"><Empty description={response ? 'No gaps in this period.' : 'Loading…'} image={Empty.PRESENTED_IMAGE_SIMPLE} /></div>}
    </PanelCard>
  );
};

// ───────────────────────────── punctuality

const PUNCTUALITY_HINT = 'Clock-ins against each site\'s clock-in window (5 min before to 15 min after by default): before = early, after = late, none = missed. Clock-ins done by the system aren\'t counted.';

export const PunctualityRateWidget = ({ settings, onSettings }) => {
  const { response, loading } = useWidget('punctuality', settings);
  return (
    <MetricTile
      label="On-time clock-ins"
      value={response ? (response.punctualPercent === null ? '-' : `${response.punctualPercent}%`) : undefined}
      loading={loading}
      alert={response?.punctualPercent !== null && response?.punctualPercent < 80}
      badge={<RangeBadge settings={settings} onChange={onSettings} fallback="monthly" options={['today', 'weekly', 'monthly', 'yearly', 'custom']} />}
      sub={response ? `${response.late} late · ${response.missed} missed · ${response.early} early` : null}
    >
      <div className="ins-note" title={PUNCTUALITY_HINT}>on time or early, of clock-ins due</div>
    </MetricTile>
  );
};

export const PunctualityTrendWidget = ({ settings, onSettings }) => {
  const { response } = useWidget('punctuality', settings, 'monthly');
  const trend = (response?.trend || []).map((x) => ({ label: x.label, 'On time': x.onTime, Early: x.early, Late: x.late, Missed: x.missed }));
  return (
    <PanelCard title="Punctuality" extra={<RangeBadge settings={settings} onChange={onSettings} fallback="monthly" options={TREND_RANGES} />}>
      <div className="ins-head-figures" title={PUNCTUALITY_HINT}>
        <span><b data-metric-value>{response ? (response.punctualPercent === null ? '-' : `${response.punctualPercent}%`) : '…'}</b> punctual {periodText(settings, 'monthly')}</span>
        <span className="ok"><b>{response?.onTime ?? '…'}</b> on time</span>
        <span className="danger"><b>{response?.late ?? '…'}</b> late</span>
        <span className="info"><b>{response?.early ?? '…'}</b> early</span>
        <span className="muted"><b>{response?.missed ?? '…'}</b> missed</span>
      </div>
      {trend.length ? (
        <div className="ins-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: '#8c8c8c', fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: '#8c8c8c', fontSize: 11 }} width={32} />
              <Tooltip contentStyle={chartTip} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="On time" stackId="p" fill={COLORS.onTime} />
              <Bar dataKey="Early" stackId="p" fill={COLORS.early} />
              <Bar dataKey="Late" stackId="p" fill={COLORS.late} />
              <Bar dataKey="Missed" stackId="p" fill={COLORS.missed} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : <div className="dw-empty"><Empty description={response ? 'No clock-ins in this period.' : 'Loading…'} image={Empty.PRESENTED_IMAGE_SIMPLE} /></div>}
    </PanelCard>
  );
};

/** Each employee's clock-ins: least punctual first */
export const EmployeePunctualityWidget = ({ settings, onSettings }) => {
  const { response } = useWidget('punctuality', settings, 'monthly');
  const rows = response?.rows || [];
  const tone = (p) => (p === null ? '' : p >= 90 ? 'ok' : p >= 75 ? 'warn' : 'danger');
  return (
    <PanelCard title="Employee punctuality" extra={<RangeBadge settings={settings} onChange={onSettings} fallback="monthly" options={TREND_RANGES} />}>
      <div className="ins-table-head"><span>Employee</span><span>On time</span><span>Late</span><span>Missed</span><span>Avg late</span></div>
      <div className="dw-list">
        {rows.length === 0 && <div className="dw-list-empty">{response ? 'No clock-ins in this period.' : 'Loading…'}</div>}
        {rows.map((r) => (
          <a key={r.employeeId} className="ins-table-row" href={`/user/employee-detail/${r.employeeId}`} target="_blank" rel="noopener noreferrer" data-punctuality-row={r.name}>
            <span className="ins-name">{r.name}</span>
            <span className={`ins-pct ${tone(r.punctualPercent)}`}>{r.punctualPercent === null ? '-' : `${r.punctualPercent}%`}<small>of {r.total}</small></span>
            <span>{r.late}</span>
            <span>{r.missed}</span>
            <span>{r.late ? `${r.avgLateMinutes} min` : '-'}</span>
          </a>
        ))}
      </div>
    </PanelCard>
  );
};

// ───────────────────────────── SIA licences

export const LicencesWidget = () => {
  const { openRows } = useDashboard();
  const { response, loading } = useWidget('licences', null);
  const columns = [
    { key: 'name', label: 'Employee', width: '1.5fr', render: (r) => r.name },
    { key: 'expiry', label: 'Expires', width: '1fr', render: (r) => fmtDate(r.expiry) },
    { key: 'state', label: '', width: '1fr', render: (r) => (r.state === 'expired' ? <Tag color="red">Expired</Tag> : r.state === 'expiring' ? <Tag color="orange">Within 30 days</Tag> : <Tag>No expiry on record</Tag>) },
    { key: 'register', label: 'SIA register', width: '1fr', render: (r) => r.registerStatus || '-' },
  ];
  const attention = (response?.expired || 0) + (response?.expiring || 0);
  return (
    <MetricTile
      label="SIA licences needing action"
      value={response ? attention : undefined}
      loading={loading}
      alert={attention > 0}
      onClick={() => openRows({ title: 'SIA licences', columns, rows: response?.rows || [], emptyText: 'Every checked licence is valid.', rowHref: (r) => `/user/employee-detail/${r.employeeId}` })}
      hint="View licences"
      sub={response ? `${response.expired} expired · ${response.expiring} expiring in 30 days · of ${response.checked} checked` : null}
    />
  );
};

// ───────────────────────────── overview figures

const OVERVIEW = {
  employees: { label: 'Active employees', value: (r) => r.employees, to: '/user/employee' },
  onDutyNow: { label: 'On duty right now', value: (r) => r.onDutyNow, to: '/user/timeline', now: true },
  fillRate: { label: 'Duties filled', value: (r) => (r.fillRate === null ? '-' : `${r.fillRate}%`), sub: (r) => `${r.duties - r.openDuties} of ${r.duties} · ${r.unconfirmedDuties} unconfirmed`, range: true, alert: (r) => r.fillRate !== null && r.fillRate < 95 },
  sites: { label: 'Sites', value: (r) => r.sites, to: '/user/site' },
  customers: { label: 'Customers', value: (r) => r.customers, to: '/user/customer' },
};
export const OverviewFigureWidget = ({ metric, settings, onSettings }) => {
  const navigate = useNavigate();
  const meta = OVERVIEW[metric];
  const { response, loading } = useWidget('overview', meta.range ? settings : null);
  return (
    <MetricTile
      label={meta.label}
      value={response ? meta.value(response) : undefined}
      loading={loading}
      alert={response ? !!meta.alert?.(response) : false}
      badge={meta.range ? <RangeBadge settings={settings} onChange={onSettings} fallback="monthly" options={['today', 'weekly', 'monthly', 'yearly', 'custom']} /> : meta.now ? <span className="metric-badge">Now</span> : null}
      onClick={meta.to ? () => navigate(meta.to) : undefined}
      hint="Open"
      sub={response && meta.sub ? meta.sub(response) : null}
    />
  );
};
